import React, { useState, useEffect, useCallback } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/toast';
import { 
  Users, CheckCircle2, Trash2, Search, 
  MapPin, Phone, GraduationCap, RefreshCw, 
  Eye, Check, X, Clock, ThumbsUp, Briefcase,
  UserPlus, Edit3, Upload, Image as ImageIcon
} from 'lucide-react';

const getPhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
  const backendBase = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:5000'
    : (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : '');
  return `${backendBase}${photo.startsWith('/') ? photo : `/${photo}`}`;
};

const STATUS_BADGES = {
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  rejected: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200 dark:border-rose-800',
};

const initialFormState = {
  name: '',
  village: '',
  block: '',
  phone: '',
  whatsapp: '',
  age: '',
  education: '',
  occupation: '',
  vision: '',
  keyPriorities: '',
  status: 'approved',
  votesCount: 0,
  photoUrl: '',
};

const AdminBhaviSarpanchContent = () => {
  const { isCollapsed } = useSidebar();
  const [candidates, setCandidates] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [blockFilter, setBlockFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // View Details Modal State
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Add / Edit Modal State
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  // Fetch villages for dropdown
  useEffect(() => {
    const fetchVillages = async () => {
      try {
        const res = await api.get('/villages');
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        // Sort villages alphabetically
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setVillages(list);
      } catch (err) {
        console.error('Error fetching villages:', err);
      }
    };
    fetchVillages();
  }, []);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/bhavi-sarpanch/admin/all?status=${statusFilter}`;
      if (blockFilter && blockFilter !== 'all') {
        url += `&block=${encodeURIComponent(blockFilter)}`;
      }
      const res = await api.get(url);
      if (res.data?.success) {
        setCandidates(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching candidates for admin:', err);
      showError('प्रत्याशियों की सूची लोड करने में त्रुटि हुई।');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, blockFilter]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Open Form Modal (Add or Edit)
  const handleOpenFormModal = (cand = null) => {
    if (cand) {
      setEditingId(cand._id);
      setFormData({
        name: cand.name || '',
        village: cand.village?._id || cand.village || '',
        block: cand.village?.block || cand.block || '',
        phone: cand.phone || '',
        whatsapp: cand.whatsapp || '',
        age: cand.age || '',
        education: cand.education || '',
        occupation: cand.occupation || '',
        vision: cand.vision || '',
        keyPriorities: Array.isArray(cand.keyPriorities) ? cand.keyPriorities.join('\n') : '',
        status: cand.status || 'approved',
        votesCount: cand.votesCount || 0,
        photoUrl: cand.photo || '',
      });
      setPhotoPreview(getPhotoUrl(cand.photo));
      setPhotoFile(null);
    } else {
      setEditingId(null);
      setFormData(initialFormState);
      setPhotoPreview('');
      setPhotoFile(null);
    }
    setFormModalOpen(true);
  };

  const handleVillageChange = (vId) => {
    const selectedV = villages.find((v) => v._id === vId);
    setFormData((prev) => ({
      ...prev,
      village: vId,
      block: selectedV?.block || prev.block,
    }));
  };

  const handlePhotoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // Submit Add or Edit Form
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.village || !formData.phone.trim() || !formData.vision.trim()) {
      showError('कृपया नाम, गाँव, फोन नंबर और विज़न अनिवार्य रूप से भरें।');
      return;
    }

    try {
      setActionLoading('form-saving');
      const data = new FormData();
      data.append('name', formData.name.trim());
      data.append('village', formData.village);
      data.append('block', formData.block);
      data.append('phone', formData.phone.trim());
      data.append('whatsapp', formData.whatsapp.trim());
      if (formData.age) data.append('age', formData.age);
      data.append('education', formData.education.trim());
      data.append('occupation', formData.occupation.trim());
      data.append('vision', formData.vision.trim());
      data.append('keyPriorities', formData.keyPriorities);
      data.append('status', formData.status);
      data.append('votesCount', formData.votesCount || 0);

      if (photoFile) {
        data.append('photo', photoFile);
      } else if (formData.photoUrl) {
        data.append('photoUrl', formData.photoUrl);
      }

      if (editingId) {
        const res = await api.put(`/bhavi-sarpanch/admin/${editingId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.success) {
          showSuccess(res.data.message || 'प्रत्याशी विवरण सफलतापूर्वक अपडेट हुआ।');
          setCandidates((prev) =>
            prev.map((c) => (c._id === editingId ? res.data.data : c))
          );
          if (selectedCandidate && selectedCandidate._id === editingId) {
            setSelectedCandidate(res.data.data);
          }
          setFormModalOpen(false);
        }
      } else {
        const res = await api.post('/bhavi-sarpanch/admin/create', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.success) {
          showSuccess(res.data.message || 'नया प्रत्याशी सफलतापूर्वक जोड़ा गया।');
          setCandidates((prev) => [res.data.data, ...prev]);
          setFormModalOpen(false);
        }
      }
    } catch (err) {
      console.error('Error saving candidate:', err);
      showError(err.response?.data?.message || 'प्रत्याशी सहेजने में त्रुटि हुई।');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    let reason = '';
    if (newStatus === 'rejected') {
      reason = window.prompt('अस्वीकृत करने का कारण लिखें (Optional):') || '';
    }

    try {
      setActionLoading(id);
      const res = await api.patch(`/bhavi-sarpanch/admin/${id}/status`, {
        status: newStatus,
        rejectionReason: reason,
      });

      if (res.data?.success) {
        showSuccess(res.data.message);
        setCandidates((prev) =>
          prev.map((c) => (c._id === id ? { ...c, status: newStatus, rejectionReason: reason } : c))
        );
        if (selectedCandidate && selectedCandidate._id === id) {
          setSelectedCandidate((prev) => ({ ...prev, status: newStatus, rejectionReason: reason }));
        }
      }
    } catch (err) {
      showError(err.response?.data?.message || 'स्टेटस अपडेट करने में त्रुटि हुई।');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`क्या आप वाकई '${name}' का आवेदन व उनके सभी वोट हटाना चाहते हैं?`)) {
      return;
    }

    try {
      setActionLoading(id);
      const res = await api.delete(`/bhavi-sarpanch/admin/${id}`);

      if (res.data?.success) {
        showSuccess(res.data.message);
        setCandidates((prev) => prev.filter((c) => c._id !== id));
        if (selectedCandidate && selectedCandidate._id === id) {
          setSelectedCandidate(null);
        }
      }
    } catch (err) {
      showError('हटाने में त्रुटि हुई।');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter candidates locally by search
  const filteredCandidates = candidates.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.village?.name?.toLowerCase().includes(q) ||
      c.village?.block?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.vision?.toLowerCase().includes(q)
    );
  });

  // Unique Blocks for filter
  const uniqueBlocks = Array.from(
    new Set(candidates.map((c) => c.village?.block || c.block).filter(Boolean))
  ).sort();

  // Stats calculation
  const totalCount = candidates.length;
  const pendingCount = candidates.filter((c) => c.status === 'pending').length;
  const approvedCount = candidates.filter((c) => c.status === 'approved').length;
  const totalVotesCount = candidates.reduce((sum, c) => sum + (c.votesCount || 0), 0);

  return (
    <>
      <SEO title="Admin - Bhavi Sarpanch Management" description="Manage village future sarpanch candidates" noindex />

      <main className={`flex-1 transition-all duration-300 min-h-screen ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="py-6 md:py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">

          {/* PAGE HEADER */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center shadow-sm">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                  भावी सरपंच प्रत्याशी प्रबंधन
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  प्रत्याशियों को जोड़ें, एडिट करें, समीक्षा व अप्रूवल दें और लाइव जनसमर्थन वोट ट्रैक करें।
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 self-start sm:self-auto">
              {/* ADD CANDIDATE BUTTON */}
              <button
                onClick={() => handleOpenFormModal(null)}
                className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs sm:text-sm flex items-center gap-1.5 shadow-md shadow-amber-600/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <UserPlus className="w-4 h-4" />
                <span>+ नया प्रत्याशी जोड़ें</span>
              </button>

              {/* REFRESH BUTTON */}
              <button
                onClick={fetchCandidates}
                className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors shadow-sm"
                title="रिफ्रेश करें"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* 4 STATS COUNTER CARDS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            
            <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold">कुल प्रत्याशी</div>
                <div className="text-xl font-black text-gray-900 dark:text-white">{totalCount}</div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold">लंबित (Pending)</div>
                <div className="text-xl font-black text-amber-600 dark:text-amber-400">{pendingCount}</div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold">स्वीकृत (Live)</div>
                <div className="text-xl font-black text-emerald-600 dark:text-emerald-400">{approvedCount}</div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                <ThumbsUp className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold">कुल जनसमर्थन</div>
                <div className="text-xl font-black text-purple-600 dark:text-purple-400">{totalVotesCount}</div>
              </div>
            </div>

          </div>

          {/* FILTERS & SEARCH BAR */}
          <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
            
            {/* Status Pills */}
            <div className="flex flex-wrap items-center gap-1.5 w-full md:w-auto">
              {[
                { key: 'all', label: 'सभी (All)' },
                { key: 'pending', label: `लंबित (${pendingCount})` },
                { key: 'approved', label: `स्वीकृत (${approvedCount})` },
                { key: 'rejected', label: 'अस्वीकृत' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    statusFilter === tab.key
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Block Filter & Search Input */}
            <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
              {uniqueBlocks.length > 0 && (
                <select
                  value={blockFilter}
                  onChange={(e) => setBlockFilter(e.target.value)}
                  className="px-3 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 w-full sm:w-auto"
                >
                  <option value="all">सभी ब्लॉक (All Blocks)</option>
                  {uniqueBlocks.map((b) => (
                    <option key={b} value={b}>{b} ब्लॉक</option>
                  ))}
                </select>
              )}

              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="नाम, गाँव या फोन से खोजें..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

          </div>

          {/* CANDIDATES TABLE */}
          {loading ? (
            <div className="py-20 text-center">
              <PageLoader message="प्रत्याशियों की सूची लोड हो रही है..." />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-12 text-center border border-gray-200 dark:border-gray-800 shadow-sm">
              <div className="w-14 h-14 bg-amber-50 dark:bg-amber-950/40 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">कोई प्रत्याशी नहीं मिला</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                चयनित फ़िल्टर के आधार पर कोई प्रत्याशी उपलब्ध नहीं है। ऊपर दिए गए बटन से नया प्रत्याशी जोड़ें।
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800 text-xs uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">
                    <tr>
                      <th className="px-5 py-3.5">प्रत्याशी विवरण</th>
                      <th className="px-5 py-3.5">गाँव व ब्लॉक</th>
                      <th className="px-5 py-3.5">योग्यता / पेशा</th>
                      <th className="px-5 py-3.5">विज़न व संकल्प</th>
                      <th className="px-5 py-3.5 text-center">जनसमर्थन (वोट)</th>
                      <th className="px-5 py-3.5 text-center">स्थिति (Status)</th>
                      <th className="px-5 py-3.5 text-right">कार्रवाई (Actions)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium">
                    {filteredCandidates.map((candidate) => (
                      <tr
                        key={candidate._id}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors"
                      >
                        {/* 1. Candidate Info */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex-shrink-0 shadow-sm">
                              {candidate.photo ? (
                                <img
                                  src={getPhotoUrl(candidate.photo)}
                                  alt={candidate.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(candidate.name) + '&background=f59e0b&color=fff&size=100';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center font-bold text-amber-600 text-sm">
                                  {candidate.name.charAt(0)}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900 dark:text-white text-sm">
                                {candidate.name}
                              </div>
                              <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                                <Phone className="w-3 h-3 text-gray-400" />
                                <a href={`tel:${candidate.phone}`} className="hover:text-primary-600">{candidate.phone}</a>
                                {candidate.age && <span className="text-gray-400 ml-1">({candidate.age} वर्ष)</span>}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* 2. Village & Block */}
                        <td className="px-5 py-4">
                          <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                            <span>गाँव {candidate.village?.name || 'Unknown'}</span>
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            {candidate.village?.block || candidate.block} ब्लॉक
                          </div>
                        </td>

                        {/* 3. Education / Occupation */}
                        <td className="px-5 py-4">
                          {candidate.education ? (
                            <div className="text-xs text-gray-800 dark:text-gray-200 font-medium flex items-center gap-1">
                              <GraduationCap className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                              <span className="line-clamp-1">{candidate.education}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                          {candidate.occupation && (
                            <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                              <Briefcase className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span className="line-clamp-1">{candidate.occupation}</span>
                            </div>
                          )}
                        </td>

                        {/* 4. Vision (Manifesto) */}
                        <td className="px-5 py-4 max-w-xs">
                          <p className="text-xs text-gray-600 dark:text-gray-300 italic line-clamp-2">
                            "{candidate.vision}"
                          </p>
                          <button
                            onClick={() => setSelectedCandidate(candidate)}
                            className="text-[11px] font-bold text-amber-600 dark:text-amber-400 hover:underline mt-0.5 flex items-center gap-0.5"
                          >
                            <Eye className="w-3 h-3" /> पूरा विज़न देखें
                          </button>
                        </td>

                        {/* 5. Votes */}
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                            <ThumbsUp className="w-3 h-3" /> {candidate.votesCount || 0}
                          </span>
                        </td>

                        {/* 6. Status Selector */}
                        <td className="px-5 py-4 text-center">
                          <select
                            value={candidate.status}
                            onChange={(e) => handleStatusUpdate(candidate._id, e.target.value)}
                            disabled={actionLoading === candidate._id}
                            className={`text-xs font-bold px-2.5 py-1 rounded-xl border cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 uppercase tracking-wider ${
                              STATUS_BADGES[candidate.status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <option value="pending">लंबित (Pending)</option>
                            <option value="approved">स्वीकृत (Approved)</option>
                            <option value="rejected">अस्वीकृत (Rejected)</option>
                          </select>
                        </td>

                        {/* 7. Action Buttons */}
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Approve Quick Button */}
                            {candidate.status !== 'approved' && (
                              <button
                                onClick={() => handleStatusUpdate(candidate._id, 'approved')}
                                disabled={actionLoading === candidate._id}
                                className="p-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                                title="स्वीकृत करें (Approve)"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}

                            {/* Reject Quick Button */}
                            {candidate.status !== 'rejected' && (
                              <button
                                onClick={() => handleStatusUpdate(candidate._id, 'rejected')}
                                disabled={actionLoading === candidate._id}
                                className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg transition-colors"
                                title="अस्वीकृत करें (Reject)"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}

                            {/* Edit Button */}
                            <button
                              onClick={() => handleOpenFormModal(candidate)}
                              className="p-1.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/60 rounded-lg transition-colors"
                              title="एडिट करें (Edit)"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>

                            {/* View Full Modal */}
                            <button
                              onClick={() => setSelectedCandidate(candidate)}
                              className="p-1.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 rounded-lg transition-colors"
                              title="विवरण देखें"
                            >
                              <Eye className="w-4 h-4" />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDelete(candidate._id, candidate.name)}
                              disabled={actionLoading === candidate._id}
                              className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              title="डिलीट करें"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* ADD / EDIT CANDIDATE MODAL */}
      {formModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={() => setFormModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-3xl max-w-2xl w-full shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  {editingId ? <Edit3 className="w-5 h-5 text-white" /> : <UserPlus className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black">
                    {editingId ? 'प्रत्याशी विवरण एडिट करें' : 'नया भावी सरपंच प्रत्याशी जोड़ें'}
                  </h3>
                  <p className="text-amber-100 text-xs">
                    {editingId ? 'प्रत्याशी की जानकारी अपडेट करें' : 'गाँव के नए भावी सरपंच का विवरण दर्ज करें'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setFormModalOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleFormSubmit} className="p-5 overflow-y-auto space-y-4 text-xs sm:text-sm">
              
              {/* Row 1: Name & Village */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    प्रत्याशी का पूरा नाम <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="उदा. कुलदीप सिंह सिहाग"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    गाँव चुनें <span className="text-rose-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.village}
                    onChange={(e) => handleVillageChange(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  >
                    <option value="">गाँव चुनें...</option>
                    {villages.map((v) => (
                      <option key={v._id} value={v._id}>
                        {v.name} ({v.block} ब्लॉक)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Block & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    ब्लॉक का नाम
                  </label>
                  <input
                    type="text"
                    placeholder="उदा. फतेहाबाद / भूना / टोहाना"
                    value={formData.block}
                    onChange={(e) => setFormData({ ...formData, block: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    मोबाइल नंबर (कॉलिंग) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="उदा. 9812345678"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
              </div>

              {/* Row 3: WhatsApp & Age */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    व्हाट्सएप नंबर (वैकल्पिक)
                  </label>
                  <input
                    type="tel"
                    placeholder="उदा. 9812345678"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    आयु (वर्ष)
                  </label>
                  <input
                    type="number"
                    min="21"
                    max="100"
                    placeholder="उदा. 35"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
              </div>

              {/* Row 4: Education & Occupation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    शैक्षणिक योग्यता (Education)
                  </label>
                  <input
                    type="text"
                    placeholder="उदा. 12वीं, B.A., M.A., LLB"
                    value={formData.education}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    व्यवसाय / पेशा (Occupation)
                  </label>
                  <input
                    type="text"
                    placeholder="उदा. समाज सेवा, कृषि, व्यापार"
                    value={formData.occupation}
                    onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
              </div>

              {/* Row 5: Status & Votes Count */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    स्वीकृति स्थिति (Status)
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs font-bold"
                  >
                    <option value="approved">स्वीकृत (Approved - Live On Website)</option>
                    <option value="pending">लंबित (Pending Review)</option>
                    <option value="rejected">अस्वीकृत (Rejected)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    जनसमर्थन वोट (Votes Count)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.votesCount}
                    onChange={(e) => setFormData({ ...formData, votesCount: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs font-bold"
                  />
                </div>
              </div>

              {/* Row 6: Photo Upload & Preview */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  प्रत्याशी की फोटो (Photo)
                </label>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  {photoPreview && (
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border border-amber-300 flex-shrink-0 shadow-sm">
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 w-full space-y-2">
                    <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl hover:border-amber-500 cursor-pointer bg-gray-50 dark:bg-gray-800/50 transition-colors">
                      <Upload className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-600 dark:text-gray-300 font-semibold">
                        कंप्यूटर से फोटो चुनें...
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoFileChange}
                        className="hidden"
                      />
                    </label>
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <input
                        type="text"
                        placeholder="या डायरेक्ट इमेज लिंक (URL) पेस्ट करें..."
                        value={formData.photoUrl}
                        onChange={(e) => {
                          setFormData({ ...formData, photoUrl: e.target.value });
                          if (e.target.value) setPhotoPreview(e.target.value);
                        }}
                        className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 7: Vision */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  चुनावी विज़न व संकल्प (Vision) <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows="3"
                  placeholder="गाँव के विकास के लिए प्रत्याशी का मुख्य लक्ष्य व विज़न लिखें..."
                  value={formData.vision}
                  onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                />
              </div>

              {/* Row 8: Key Priorities */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  मुख्य प्राथमिकताएं (Key Priorities)
                </label>
                <textarea
                  rows="3"
                  placeholder="प्रत्येक लाइन में एक प्राथमिकता लिखें (उदा.&#10;गाँव में शुद्ध पेयजल की व्यवस्था&#10;सरकारी स्कूल में बेहतर सुविधाएं&#10;गलियों में सोलर लाइटें लगवाना)"
                  value={formData.keyPriorities}
                  onChange={(e) => setFormData({ ...formData, keyPriorities: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-xs font-mono"
                />
              </div>

              {/* Modal Footer Buttons */}
              <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setFormModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === 'form-saving'}
                  className="px-6 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-md shadow-amber-600/20 transition-all flex items-center gap-1.5"
                >
                  {actionLoading === 'form-saving' ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>सहेज रहे हैं...</span>
                    </>
                  ) : (
                    <span>{editingId ? 'अपडेट सुरक्षित करें' : 'प्रत्याशी जोड़ें'}</span>
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* FULL CANDIDATE DETAILS MODAL */}
      {selectedCandidate && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={() => setSelectedCandidate(null)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-5 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">
                  प्रत्याशी पूर्ण विवरण
                </span>
                <h3 className="text-lg font-black mt-1">{selectedCandidate.name}</h3>
                <p className="text-amber-100 text-xs">
                  गाँव {selectedCandidate.village?.name} ({selectedCandidate.village?.block || selectedCandidate.block} ब्लॉक)
                </p>
              </div>
              <button 
                onClick={() => setSelectedCandidate(null)}
                className="p-1.5 rounded-full hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              
              {/* Photo & Basic Details */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-2xl">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-700 border flex-shrink-0">
                  {selectedCandidate.photo ? (
                    <img src={getPhotoUrl(selectedCandidate.photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-bold text-amber-600 text-lg">
                      {selectedCandidate.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="text-xs space-y-1">
                  <div><strong>फोन:</strong> <a href={`tel:${selectedCandidate.phone}`} className="text-primary-600 font-bold">{selectedCandidate.phone}</a></div>
                  <div><strong>व्हाट्सएप:</strong> {selectedCandidate.whatsapp || selectedCandidate.phone}</div>
                  <div><strong>आयु:</strong> {selectedCandidate.age || '-'} वर्ष &bull; <strong>शिक्षा:</strong> {selectedCandidate.education || '-'}</div>
                  <div><strong>व्यवसाय:</strong> {selectedCandidate.occupation || '-'}</div>
                </div>
              </div>

              {/* Vision Box */}
              <div>
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                  चुनावी विज़न व संकल्प:
                </h4>
                <div className="p-3.5 bg-amber-50/60 dark:bg-gray-800 rounded-2xl border border-amber-200/60 dark:border-gray-700 text-xs text-gray-800 dark:text-gray-200 leading-relaxed italic">
                  "{selectedCandidate.vision}"
                </div>
              </div>

              {/* Key Priorities */}
              {selectedCandidate.keyPriorities && selectedCandidate.keyPriorities.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    मुख्य प्राथमिकताएं:
                  </h4>
                  <div className="space-y-1">
                    {selectedCandidate.keyPriorities.map((item, idx) => (
                      <div key={idx} className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection reason if any */}
              {selectedCandidate.status === 'rejected' && selectedCandidate.rejectionReason && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-xl text-xs text-rose-700 dark:text-rose-300">
                  <strong>अस्वीकृति का कारण:</strong> {selectedCandidate.rejectionReason}
                </div>
              )}

              {/* Quick Actions in Modal */}
              <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div className="text-xs font-bold text-gray-500">
                  कुल जनसमर्थन: <span className="text-purple-600">{selectedCandidate.votesCount || 0} वोट</span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Edit button in modal */}
                  <button
                    onClick={() => {
                      const cand = selectedCandidate;
                      setSelectedCandidate(null);
                      handleOpenFormModal(cand);
                    }}
                    className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                  >
                    <Edit3 className="w-3.5 h-3.5" /> एडिट करें
                  </button>

                  {selectedCandidate.status !== 'approved' && (
                    <button
                      onClick={() => handleStatusUpdate(selectedCandidate._id, 'approved')}
                      className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                    >
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  {selectedCandidate.status !== 'rejected' && (
                    <button
                      onClick={() => handleStatusUpdate(selectedCandidate._id, 'rejected')}
                      className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                    >
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default function AdminBhaviSarpanch() {
  return (
    <AdminLayout>
      <AdminBhaviSarpanchContent />
    </AdminLayout>
  );
}
